import { get } from "lodash";
import { useQuery } from "react-query";

import { mapLicenseRequestToRow } from "~/entity/Licenses/lib/mapLicenseRequestToRow";
import {
  ILicenseRequestApi,
  ILicenseRequestsListParams,
  ILicenseRequestsListResponse,
  LicenseRequestApiStatus,
} from "~/entity/Licenses/model/schema";

import { useLicenseRequests } from "./useLicenseRequests";

interface UseGetLicenseRequestsListParams {
  params: ILicenseRequestsListParams;
  isAdmin: boolean;
  dealerId?: string | null;
  mergeFinalStatuses?: boolean;
}

const FINAL_STATUSES: LicenseRequestApiStatus[] = ["APPROVED", "REJECTED"];
const FETCH_PAGE_SIZE = 100;

const sortByCreatedAtDesc = (
  left: ILicenseRequestApi,
  right: ILicenseRequestApi,
) => {
  const leftDate = left.createdAt || left.requestDate || "";
  const rightDate = right.createdAt || right.requestDate || "";

  if (leftDate === rightDate) {
    return 0;
  }

  return leftDate > rightDate ? -1 : 1;
};

const fetchMergedFinalStatuses = async (
  getList: (params?: ILicenseRequestsListParams) => Promise<{
    data: ILicenseRequestsListResponse;
  }>,
  base: ILicenseRequestsListParams,
) => {
  const collected: ILicenseRequestApi[] = [];

  for (let index = 0; index < FINAL_STATUSES.length; index += 1) {
    const status = FINAL_STATUSES[index];
    let page = 0;
    let loaded = 0;
    let total = Number.POSITIVE_INFINITY;

    while (loaded < total) {
      const response = await getList({
        ...base,
        status,
        page,
        count: FETCH_PAGE_SIZE,
      });
      const requests = get(
        response,
        "data.requests",
        [],
      ) as ILicenseRequestApi[];
      total = get(response, "data.total", 0) as number;
      collected.push(...requests);
      loaded += requests.length;

      if (!requests.length) {
        break;
      }

      page += 1;
    }
  }

  collected.sort(sortByCreatedAtDesc);

  const page = base.page || 0;
  const count = base.count || 25;
  const start = page * count;

  return {
    data: {
      requests: collected.slice(start, start + count),
      total: collected.length,
    },
  };
};

export const useGetLicenseRequestsList = ({
  params,
  isAdmin,
  dealerId,
  mergeFinalStatuses = false,
}: UseGetLicenseRequestsListParams) => {
  const { queryKeys, getLicenseRequestsList } = useLicenseRequests();
  const queryParams = isAdmin
    ? params
    : { ...params, dealerId: Number(dealerId) };
  const enabled = isAdmin || Boolean(dealerId);

  const query = useQuery({
    queryKey: [...queryKeys.list(queryParams), mergeFinalStatuses],
    queryFn: () =>
      mergeFinalStatuses
        ? fetchMergedFinalStatuses(getLicenseRequestsList, queryParams)
        : getLicenseRequestsList(queryParams),
    enabled,
  });

  const requests = (
    get(query.data, "data.requests", []) as ILicenseRequestApi[]
  ).map(mapLicenseRequestToRow);
  const total = get(query.data, "data.total", 0) as number;

  return {
    ...query,
    requests,
    total,
  };
};
